import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GenericListPage } from '../../components/generic';
import { useClientServices, ClientService } from './useData';
import { moduleConfig, getColumns, getTableActions, getToolbarIcon } from './config';
import ServiceRenewalRemindersPanel from './ServiceRenewalRemindersPanel';
import { cn } from '../../lib/utils';

const SPLITTER_STORAGE_KEY = 'client-services-reminders-splitter';
const SPLITTER_MIN = 40; // list min %
const SPLITTER_MAX = 85; // list max % (panel at least 15%)

const List: React.FC = () => {
  const [remindersPanelOpen, setRemindersPanelOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ClientService | null>(null);
  const [splitterPosition, setSplitterPosition] = useState(() => {
    try {
      const v = sessionStorage.getItem(SPLITTER_STORAGE_KEY);
      if (v != null) {
        const n = Number(v);
        if (Number.isFinite(n) && n >= SPLITTER_MIN && n <= SPLITTER_MAX) return n;
      }
    } catch {
      // ignore
    }
    return 70;
  });
  const [isResizing, setIsResizing] = useState(false);
  const { data: clientServices, mutate } = useClientServices();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasRestoredFromParams, setHasRestoredFromParams] = useState(false);

  const handleSplitterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const container = document.querySelector('.client-services-split-container');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitterPosition(Math.max(SPLITTER_MIN, Math.min(SPLITTER_MAX, newPosition)));
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SPLITTER_STORAGE_KEY, String(splitterPosition));
    } catch {
      // ignore
    }
  }, [splitterPosition]);

  const openRenewalRemindersPanel = useCallback(
    (service: ClientService) => {
      setSelectedService(service);
      setRemindersPanelOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('reminders', '1');
        next.set('serviceId', String(service.clientServicetId));
        return next;
      });
    },
    [setSearchParams]
  );

  const closeRenewalRemindersPanel = useCallback(() => {
    setRemindersPanelOpen(false);
    setSelectedService(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('reminders');
      next.delete('serviceId');
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (hasRestoredFromParams) return;
    const remindersFlag = searchParams.get('reminders');
    const serviceIdParam = searchParams.get('serviceId');
    if (remindersFlag === '1' && serviceIdParam && clientServices?.length > 0) {
      const id = parseInt(serviceIdParam, 10);
      const service = clientServices.find((s) => s.clientServicetId === id);
      if (service) {
        setSelectedService(service);
        setRemindersPanelOpen(true);
        setHasRestoredFromParams(true);
      }
    }
  }, [searchParams, clientServices, hasRestoredFromParams]);

  return (
    <div className={cn('flex h-full min-h-0 relative', remindersPanelOpen && 'client-services-split-container')}>
      <div
        className={cn('min-h-0 overflow-hidden', remindersPanelOpen ? 'flex-shrink-0' : 'flex-1 min-w-0')}
        style={remindersPanelOpen ? { width: `${splitterPosition}%` } : undefined}
      >
        <GenericListPage<ClientService>
          useData={useClientServices}
          listContext={{ onOpenRenewalRemindersPanel: openRenewalRemindersPanel }}
          config={{
            ...moduleConfig,
            getColumns,
            getToolbarIcon,
            getTableActions,
          }}
        />
      </div>
      {remindersPanelOpen && (
        <>
          <div
            className={cn(
              'absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors z-10 shrink-0 opacity-50 hover:opacity-100 bg-gray-300 dark:bg-gray-600',
              isResizing && '!opacity-100'
            )}
            style={{ left: `${splitterPosition}%`, transform: 'translateX(-50%)' }}
            onMouseDown={handleSplitterMouseDown}
            role="separator"
            aria-label="Resize panels"
          >
            <div className="absolute inset-y-0 -left-1 -right-1" />
          </div>
          <div
            className="flex-shrink-0 flex flex-col min-h-0 min-w-0 overflow-hidden"
            style={{ width: `${100 - splitterPosition}%` }}
          >
            <ServiceRenewalRemindersPanel
              open
              onClose={closeRenewalRemindersPanel}
              serviceItem={selectedService}
              onReminderAdded={mutate}
              asPanel
            />
          </div>
        </>
      )}
    </div>
  );
};

export default List;

